/**
 * Hook für aggregierten Workshop-Fortschritt.
 *
 * Lädt für einen Workshop die Modul-States aller fünf Module (über
 * dieselben TanStack-Query-Keys wie die Modul-Pages, daher geteilter Cache)
 * und liefert daraus `progress` plus den „nächsten sinnvollen Schritt"
 * (in-progress-Modul, sonst erstes pending-Modul, sonst null wenn alles
 * fertig).
 */
import { useQueries } from "@tanstack/react-query";

import {
  listModuleSteps,
  type ModuleKey,
  type ModuleState,
} from "@/api/workshop-modules";

import {
  computeModuleProgress,
  MODULE_ORDER,
  type ModuleProgress,
} from "./moduleStatus";

export function useWorkshopProgress(workshopId: string): {
  progress: Record<ModuleKey, ModuleProgress>;
  nextModule: ModuleKey | null;
  isLoading: boolean;
} {
  const queries = useQueries({
    queries: MODULE_ORDER.map((key) => ({
      queryKey: ["modules", key, "steps", workshopId],
      queryFn: () => listModuleSteps(workshopId, key),
      enabled: !!workshopId,
      staleTime: 5_000,
    })),
  });
  const states: ModuleState[] = queries.flatMap((q) => q.data ?? []);
  const progress = computeModuleProgress(states);

  const inProgress = MODULE_ORDER.find(
    (k) => progress[k].status === "in_progress",
  );
  const pending = MODULE_ORDER.find((k) => progress[k].status === "pending");
  const nextModule = inProgress ?? pending ?? null;

  return {
    progress,
    nextModule,
    isLoading: queries.some((q) => q.isPending),
  };
}
